import { HStack, PageContainer, PageHeader, Spread, Text, VStack } from '@/components/Atoms'
import { Badge } from '@/components/Badge'
import { PageError } from '@/components/PageError'
import { PageLoader } from '@/components/PageLoader'
import usePermissions from '@/hooks/permissions/usePermissions'
import { useBlockedOrderPayerLastDecision } from '@/hooks/queries/credit/useBlockedOrders'
import { useCustomerDetail } from '@/hooks/queries/credit/useCustomers'
import { formatAddress } from '@/lib/utils/addresses'
import { cn } from '@repo/ui/lib/utils'
import { snappyEaseInOut } from '@repo/ui/lib/animations'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@repo/ui/components'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useLayoutEffect, useState } from 'react'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { BlockedOrderDecisionBatchCard } from '../../blocked-orders/-components/BlockedOrderDecisionBatchCard'
import { BankruptcyNote } from '../-components/BankruptcyNote'
import { CreditDataValidationNote } from '../-components/CreditDataValidationNote'
import { CreditExposureCard } from '../-components/CreditExposureCard'
import { CreditIndexScoreCard } from '../-components/CreditIndexScoreCard'
import { CreditOverviewCard } from '../-components/CreditOverviewCard'
import { CustomerAddress } from '../-components/CustomerAddress'
import { CustomerDetailsCard } from '../-components/CustomerDetailsCard'
import { ExportCustomerButton } from '../-components/ExportCustomerButton'
import { ExternalCreditScoreCard } from '../-components/ExternalCreditScoreCard'
import { FinancialInformationCard } from '../-components/FinancialInformationCard'
import { NewAccountCreditLimitCard } from '../-components/NewAccountCreditLimitCard'
import { OrderInformationCard } from '../-components/OrderInformationCard'
import { PaymentInformationCard } from '../-components/PaymentInformationCard'
import { PrepareClrButton } from '../-components/PrepareClrButton'
import {
  CLR_DRAWER_ANIMATION_MS,
  CLR_DRAWER_INLINE_MIN_WIDTH,
  CLR_DRAWER_WIDTH,
  PrepareClrDrawer,
} from '../-components/PrepareClrDrawer'
import { SalesTrendCard } from '../-components/SalesTrendCard'

// The drawer lives in the URL, not in state: the old /prepare-clr page redirects into it, the CLR
// list opens it from elsewhere, and a reload keeps the preparer's place.
const customerDetailSearchSchema = z.object({
  prepareClr: z.boolean().optional(),
  // Present when preparing a sent-back or unowned review: prefills the form and resubmits.
  reviewId: z.number().optional(),
})

export const Route = createFileRoute('/_auth/_modules/credit/customers/$erp/')({
  component: RouteComponent,
  validateSearch: customerDetailSearchSchema,
})

function RouteComponent() {
  const { erp } = Route.useParams()

  // Remounted per customer: TanStack reuses a route's component across a param change, which would
  // carry the drawer's draft from one customer to the next.
  return <CustomerOverview key={erp} erp={erp} />
}

function CustomerOverview({ erp }: { erp: string }) {
  const { t } = useTranslation(['credit', 'common'])
  const { prepareClr, reviewId } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const { canUser } = usePermissions()
  const detailQuery = useCustomerDetail(erp)
  // The payer's last release or rejection, as the inbox shows it under a pending order (CT-41):
  // what was last decided for this account, a click from the decision's page. Only for someone who
  // can see blocked orders at all.
  const lastDecision = useBlockedOrderPayerLastDecision(erp, {
    enabled: canUser('credit_blocked_orders_view'),
  }).data

  const isDrawerOpen = Boolean(prepareClr)

  // The form's request is what a plain overview visit should not pay for, so only the form waits
  // for the first open — the wrapper around it is always mounted at zero width, which is what makes
  // that first open animate. Afterwards the form stays mounted, so closing keeps the draft.
  const [wasDrawerOpened, setWasDrawerOpened] = useState(isDrawerOpen)
  if (isDrawerOpen && !wasDrawerOpened) setWasDrawerOpened(true)

  const [hasDraft, setHasDraft] = useState(false)

  // Whether the header's own toggle is still on screen; the bar at the foot of the page takes over
  // when it is not. A callback ref, not useRef: the header does not exist on the first render.
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null)
  const [isTriggerVisible, setIsTriggerVisible] = useState(true)
  useEffect(() => {
    if (!triggerEl) return
    const observer = new IntersectionObserver(
      ([entry]) => setIsTriggerVisible(entry.isIntersecting),
      { threshold: 0 },
    )
    observer.observe(triggerEl)
    return () => observer.disconnect()
  }, [triggerEl])

  // Which shape the drawer takes is decided by the width the cards and the panel have to share, so
  // it is measured on their row rather than on the window.
  const [rowEl, setRowEl] = useState<HTMLElement | null>(null)
  const [rowWidth, setRowWidth] = useState<number>()
  // Layout, not passive: a deep link into an open drawer would otherwise paint one frame of the
  // panel as a column before it became an overlay.
  useLayoutEffect(() => {
    if (!rowEl) return
    setRowWidth(rowEl.getBoundingClientRect().width)
    const observer = new ResizeObserver(([entry]) => setRowWidth(entry.contentRect.width))
    observer.observe(rowEl)
    return () => observer.disconnect()
  }, [rowEl])

  // Undefined only for the render that mounts the row, where the column is the right guess.
  const isOverlay = rowWidth !== undefined && rowWidth < CLR_DRAWER_INLINE_MIN_WIDTH

  // Only the overlay needs the bar: a column sits beside its cards with its own X in reach, and a
  // bar across the foot of the page would be a second control for a panel already right there. It
  // carries whatever the header would, so it asks the same question of permissions.
  const showStickyFooter = isOverlay && !isTriggerVisible && canUser('credit_limit_reviews_view')

  // The panel has to stay put for the length of the collapse, then stop being there: a zero-width
  // flex child still claims the row's gap-6, and an overlay parked off screen is still a form in
  // the tab order.
  const [isCollapsed, setIsCollapsed] = useState(!isDrawerOpen)
  // Uncollapsed during render, not in an effect: an effect lands after the paint, so the slide
  // would spend its first frame still display:none.
  if (isDrawerOpen && isCollapsed) setIsCollapsed(false)
  useEffect(() => {
    if (isDrawerOpen) return
    // A fallback, not the mechanism: onAnimationComplete below normally gets here first. Timed
    // well clear of the slide so jank cannot pop the panel out mid-collapse, and only there to
    // guarantee it leaves the layout when no frames run at all — a background tab, say.
    const timer = setTimeout(() => setIsCollapsed(true), CLR_DRAWER_ANIMATION_MS * 4)
    return () => clearTimeout(timer)
  }, [isDrawerOpen])

  // Only prepareClr is dropped. reviewId is what keys the form inside, so clearing it here would
  // flip that key from the review to 'new' and remount the form — destroying the draft and the
  // uploaded evidence list that keeping the drawer mounted exists to preserve.
  const closeDrawer = () => navigate({ search: { prepareClr: undefined, reviewId }, replace: true })

  // The claim path passes the review to take over; a fresh preparation passes nothing and keeps
  // whatever reviewId is already in the URL (a reopened draft is still the same review).
  const openDrawer = (claimReviewId?: number) =>
    navigate({ search: { prepareClr: true, reviewId: claimReviewId ?? reviewId }, replace: true })

  // The mutations invalidate the review queries and raise their own toast; the customer detail is
  // ours to refresh so the cards reflect the new review.
  const onClrSubmitted = () => {
    // Not closeDrawer: there is no draft left to protect, and the review it was for can no longer
    // be resubmitted, so the URL must not reopen onto it.
    navigate({ search: { prepareClr: undefined, reviewId: undefined }, replace: true })
    queryClient.invalidateQueries({ queryKey: ['customerDetail', erp] })
  }

  if (detailQuery.isPending) return <PageLoader />
  if (detailQuery.error || !detailQuery.data) return <PageError />

  const detail = detailQuery.data

  // Second header line: "Legal Name - Address" (each part dropped when absent).
  const { street, city, region, postalCode } = detail.address
  const hasAddress =
    [formatAddress(street, null, city, region), postalCode].filter(Boolean).join('').trim() !== ''

  return (
    <TooltipProvider delayDuration={150}>
      {/* The bar has to reach the bottom edge, and PageContainer's space-y-8 outranks a negative
          margin on specificity, so the padding is dropped instead. */}
      <PageContainer maxWidth="wide" className={cn(showStickyFooter && 'pb-0')}>
        <Spread align="start" inert={isOverlay && isDrawerOpen}>
          <VStack gap="xs">
            <HStack gap="sm" className="items-center">
              <PageHeader>{detail.businessName ?? erp}</PageHeader>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge as="span" tone="alpha" inverted className="text-sm">
                    #{erp}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{t('customers.detail.erpNumber')}</TooltipContent>
              </Tooltip>
            </HStack>
            {(detail.legalName || hasAddress) && (
              <HStack gap="sm" className="items-baseline">
                {detail.legalName && (
                  <Text size="xs" tone="muted">
                    {detail.legalName}
                  </Text>
                )}
                {detail.legalName && hasAddress && (
                  <Text size="xs" tone="muted" aria-hidden>
                    &middot;
                  </Text>
                )}
                {hasAddress && (
                  <CustomerAddress
                    street={street}
                    city={city}
                    province={region}
                    postalCode={postalCode}
                  />
                )}
              </HStack>
            )}
          </VStack>

          <div ref={setTriggerEl} className="shrink-0">
            <HStack gap="sm">
              <ExportCustomerButton erp={erp} />
              <PrepareClrButton
                erp={erp}
                isDrawerOpen={isDrawerOpen}
                hasDraft={hasDraft}
                onOpen={openDrawer}
                onClose={closeDrawer}
              />
            </HStack>
          </div>
        </Spread>

        {/* Above the cards, so the account's state is read before anything is proposed for it.
            Unwrapped: it renders nothing for a solvent account, and a wrapper would still take a
            slot in PageContainer's space-y. No `inert` either — it holds nothing focusable, so an
            overlay above it has nothing to take out of the tab order. The drawer carries its own
            copy, since as an overlay it puts this whole page behind a backdrop. */}
        <BankruptcyNote ciScore={detail.externalCreditScore?.ciScore} />
        {/* Same slot and same reasoning: SAP or the score import handed us a value outside the
            known options, and that has to be read before anything is proposed for the account. */}
        <CreditDataValidationNote
          {...detail.dataValidation}
          riskClass={detail.creditOverview.current.riskClass}
          ciScore={detail.externalCreditScore?.ciScore}
        />

        <div ref={setRowEl} className="flex flex-col gap-6 lg:flex-row lg:items-start">
          {/* A query container, not viewport breakpoints: the columns respond to the width the
              drawer leaves them. Separate element from the flex row it wraps, since container-type
              establishes a container for descendants and not for the element itself. */}
          <div className="@container min-w-0 flex-1" inert={isOverlay && isDrawerOpen}>
            <div className="@2xl:flex-row @2xl:items-start flex flex-col gap-6">
              <div className="@2xl:w-1/3 flex flex-col gap-6">
                <CustomerDetailsCard
                  erp={erp}
                  details={detail.details}
                  isCreditBlocked={detail.creditOverview.isCreditBlocked}
                />
                <OrderInformationCard
                  erp={erp}
                  orders={detail.orders}
                  creditExposure={detail.creditExposure}
                />
                <PaymentInformationCard erp={erp} payments={detail.payments} />
                <ExternalCreditScoreCard externalCreditScore={detail.externalCreditScore} />
                <FinancialInformationCard erp={erp} />
              </div>

              <div className="@2xl:w-2/3 flex flex-col gap-6">
                {detail.newAccountCreditLimit && (
                  <NewAccountCreditLimitCard newAccount={detail.newAccountCreditLimit} />
                )}

                <CreditOverviewCard
                  creditOverview={detail.creditOverview}
                  payments={detail.payments}
                  externalCreditScore={detail.externalCreditScore}
                />

                {/* A new account has no history, so every one of these would be empty. */}
                {!detail.newAccountCreditLimit && (
                  <>
                    <CreditExposureCard
                      erp={erp}
                      exposure={detail.creditExposure}
                      recommendedCreditLimit={detail.creditOverview.recommendation.creditLimit}
                    />

                    <SalesTrendCard
                      erp={erp}
                      salesTrend={detail.salesTrend}
                      variance={detail.salesTrendVariance}
                    />

                    <CreditIndexScoreCard scoreHistory={detail.scoreHistory} />
                  </>
                )}

                {lastDecision && (
                  <BlockedOrderDecisionBatchCard
                    batch={lastDecision}
                    title={t('blockedOrders.decisionBatches.lastBlockedOrderTitle')}
                    isSummaryOnly
                    historyPayerErp={erp}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Tied to the panel's own isCollapsed so neither outlives the other. Fixed rather than
              bounded to the row: what sits behind an overlay this size is the whole app. */}
          {isOverlay && !isCollapsed && (
            <motion.div
              aria-hidden
              onClick={closeDrawer}
              className="bg-background/40 fixed inset-0 z-30 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: isDrawerOpen ? 1 : 0 }}
              transition={{ duration: CLR_DRAWER_ANIMATION_MS / 1000, ease: snappyEaseInOut }}
            />
          )}

          {/* One element in both shapes, hidden rather than unmounted: remounting to move between
              a column and an overlay would drop the form mid-sentence on a window resize. The
              column animates width so the cards give up the space; the overlay slides on transform
              so they do not reflow. Sticky belongs here, not on the panel — overflow-hidden on an
              ancestor confines a sticky descendant to it. */}
          <motion.aside
            aria-label={t('clr.prepare.pageHeader')}
            className={cn(
              'max-w-full overflow-hidden',
              isOverlay
                ? 'fixed inset-y-0 right-0 z-50 shadow-[-8px_0_32px_0px_rgba(0,0,0,0.18)]'
                : // Unelevated: as a column the panel is beside the cards, not over them, and the
                  // ring it already draws is enough to separate the two. rounded-lg keeps the
                  // silhouette matching the panel.
                  'sticky top-8 shrink-0 self-start rounded-lg',
              isCollapsed && 'hidden',
            )}
            // No mount animation: on a deep link the drawer is open from the first paint and should
            // simply be there. In-session opens animate because the element already exists at zero
            // width, which is the whole reason it is mounted before it is ever opened.
            initial={false}
            animate={{
              // The open state is the same geometry in both shapes, so a resize that crosses the
              // threshold with the drawer open changes only where the panel is anchored: there is
              // nothing left for the animation to travel, and it swaps over in place.
              width: isOverlay || isDrawerOpen ? CLR_DRAWER_WIDTH : 0,
              x: isOverlay && !isDrawerOpen ? CLR_DRAWER_WIDTH : 0,
              opacity: isDrawerOpen ? 1 : 0,
            }}
            transition={{ duration: CLR_DRAWER_ANIMATION_MS / 1000, ease: snappyEaseInOut }}
            onAnimationComplete={() => {
              if (!isDrawerOpen) setIsCollapsed(true)
            }}
          >
            {wasDrawerOpened && (
              <PrepareClrDrawer
                erp={erp}
                ciScore={detail.externalCreditScore?.ciScore}
                dataValidation={detail.dataValidation}
                riskClass={detail.creditOverview.current.riskClass}
                isOpen={isDrawerOpen}
                reviewId={reviewId}
                variant={isOverlay ? 'overlay' : 'inline'}
                onClose={closeDrawer}
                onSubmitted={onClrSubmitted}
                onDraftChange={setHasDraft}
              />
            )}
          </motion.aside>
        </div>

        {/* The header's own button again, for the stretch of the page where the header has
            scrolled away. Sticky, not fixed: the app scrolls inside a box that starts after the nav
            sidebar, so this pins to the bottom of that without knowing its width. */}
        {showStickyFooter && (
          <motion.div
            // Barely there: it arrives mid-scroll, and more of a rise reads as the page moving.
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, ease: snappyEaseInOut }}
            className="bg-background-level-2 sticky bottom-0 z-20 -mx-8 flex h-16 items-center justify-end border-t px-8"
          >
            <PrepareClrButton
              erp={erp}
              isDrawerOpen={isDrawerOpen}
              hasDraft={hasDraft}
              onOpen={openDrawer}
              onClose={closeDrawer}
            />
          </motion.div>
        )}
      </PageContainer>
    </TooltipProvider>
  )
}
